import React, { useEffect, useMemo, useState } from "react";
import '../AnnouncmentManagement/Annoucement.css';
import AuthService from '../../../api/services/AuthService';
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import moment from "moment";

import {
  STATIC_CATEGORIES,
  STATIC_ANNOUNCEMENTS,
  FALLBACK_DETAIL_IMG,
  FALLBACK_EMPTY_IMG,
  normalizeCategory,
  normalizeAnnouncement,
  extractList,
  announcementCoverUrl,
} from "./staticAnnouncements";

const PAGE_SIZE = 10;

const ImgWithFallback = ({ src, fallback, alt, className }) => (
  <img
    className={className}
    src={src || fallback}
    alt={alt || ""}
    onError={(e) => {
      if (e.currentTarget.dataset.fallback === "1") return;
      e.currentTarget.dataset.fallback = "1";
      e.currentTarget.src = fallback || FALLBACK_DETAIL_IMG;
    }}
  />
);

const Announcement = () => {
  const [categoryList, setCategoryList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [announcementList, setAnnouncementList] = useState([]);
  const [announcementDetails, setAnnouncementDetails] = useState({});
  const [relatedAnnouncement, setRelatedAnnouncement] = useState([]);
  const [page, setPage] = useState(1);
  const { category: categoryId, id: announcementId } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    handleCategoryAnnouncement();
  }, []);

  useEffect(() => {
    if (announcementId) {
      handleAnnouncementDetails(announcementId);
    }
  }, [announcementId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [categoryId, announcementId, page]);

  const handleCategoryAnnouncement = async () => {
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getAnnouncementCategoryList();
      const rows = extractList(result?.data ?? result).map((item, i) => normalizeCategory(item, i));
      if (result?.success && rows.length > 0) {
        setCategoryList(rows);
      } else {
        setCategoryList(STATIC_CATEGORIES.map((c, i) => normalizeCategory(c, i)));
      }
    } catch {
      setCategoryList(STATIC_CATEGORIES.map((c, i) => normalizeCategory(c, i)));
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleAnnounceList = async (catId) => {
    if (!catId) return;
    const categoryTitle = categoryList.find((c) => String(c._id) === String(catId))?.title;
    const withCat = (item) => normalizeAnnouncement({ ...item, categoryTitle }, catId);
    LoaderHelper.loaderStatus(true);
    try {
      if (String(catId).startsWith("cat_")) {
        const filtered = STATIC_ANNOUNCEMENTS
          .filter((item) => item.categoryId === catId)
          .map(withCat);
        setAnnouncementList(filtered);
        return;
      }

      const result = await AuthService.getAnnouncementList(catId);
      const rows = extractList(result?.data ?? result).map(withCat);
      if (result?.success && rows.length > 0) {
        setAnnouncementList(rows);
      } else {
        const byTitle = STATIC_CATEGORIES.find(
          (c) => String(c._id) === String(catId) || c.title.toLowerCase() === String(catId).toLowerCase()
        );
        const fallbackId = byTitle?._id || catId;
        setAnnouncementList(
          STATIC_ANNOUNCEMENTS
            .filter((item) => item.categoryId === fallbackId)
            .map(withCat)
        );
      }
    } catch {
      const filtered = STATIC_ANNOUNCEMENTS
        .filter((item) => item.categoryId === catId)
        .map(withCat);
      setAnnouncementList(filtered);
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleAnnouncementDetails = async (annId) => {
    LoaderHelper.loaderStatus(true);
    try {
      const staticItem = STATIC_ANNOUNCEMENTS.find((item) => item._id === annId);
      if (staticItem) {
        const normalized = normalizeAnnouncement(staticItem);
        setAnnouncementDetails(normalized);
        setRelatedAnnouncement(
          STATIC_ANNOUNCEMENTS
            .filter((item) => item.categoryId === staticItem.categoryId && item._id !== staticItem._id)
            .map((item) => normalizeAnnouncement(item))
        );
        return;
      }

      const result = await AuthService.announcementView(annId);
      const detailRaw =
        result?.data?.getParticularAnnouncementdata
        || result?.data?.announcement
        || (result?.data && !Array.isArray(result.data) ? result.data : null);
      if (result?.success && detailRaw?._id) {
        const detail = normalizeAnnouncement(detailRaw);
        setAnnouncementDetails(detail);
        const relatedRaw = extractList(
          result?.data?.relatedAnnouncementListing || result?.data?.related || []
        );
        setRelatedAnnouncement(relatedRaw.map((item) => normalizeAnnouncement(item, detail.categoryId)));
      } else {
        const fallbackItem = STATIC_ANNOUNCEMENTS.find((item) => item._id === annId);
        if (fallbackItem) {
          setAnnouncementDetails(normalizeAnnouncement(fallbackItem));
          setRelatedAnnouncement(
            STATIC_ANNOUNCEMENTS
              .filter((item) => item.categoryId === fallbackItem.categoryId && item._id !== fallbackItem._id)
              .map((item) => normalizeAnnouncement(item))
          );
        }
      }
    } catch {
      const fallbackItem = STATIC_ANNOUNCEMENTS.find((item) => item._id === annId);
      if (fallbackItem) {
        setAnnouncementDetails(normalizeAnnouncement(fallbackItem));
      }
    } finally {
      LoaderHelper.loaderStatus(false);
    }
  };

  const activeCategoryId = categoryId || (categoryList?.length > 0 ? categoryList[0]._id : null);
  const currentCategoryName = categoryList.find(c => c._id === activeCategoryId)?.title || "Announcements";

  useEffect(() => {
    if (activeCategoryId) {
      handleAnnounceList(activeCategoryId);
    }
  }, [activeCategoryId]);

  const filteredAnnouncements = useMemo(() => {
    if (!announcementList?.length) return [];
    const q = searchTerm.trim().toLowerCase();
    if (!q) return announcementList;
    return announcementList.filter((item) => {
      const title = String(item?.title || "").toLowerCase();
      const desc = String(item?.description || "").toLowerCase();
      return title.includes(q) || desc.includes(q);
    });
  }, [announcementList, searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [activeCategoryId, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredAnnouncements.length / PAGE_SIZE));
  const pagedAnnouncements = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredAnnouncements.slice(start, start + PAGE_SIZE);
  }, [filteredAnnouncements, page]);

  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const set = new Set([1, totalPages, page, page - 1, page + 1]);
    return [...set].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
  }, [page, totalPages]);

  const shareAnnouncement = async () => {
    const url = window.location.href;
    const title = announcementDetails?.title || "AGCX Announcement";
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        return;
      }
      await navigator.clipboard.writeText(url);
    } catch {
      /* user cancelled */
    }
  };

  const detailCover = announcementCoverUrl(announcementDetails, { fallback: true }) || FALLBACK_DETAIL_IMG;

  // Dynamic Helmet Meta Tags
  const helmetTitle = useMemo(() => {
    if (announcementId) {
      return `${announcementDetails?.title || "Announcement Details"} | AGCX`;
    }
    return `${currentCategoryName || "Announcements"} | AGCX`;
  }, [announcementId, announcementDetails, currentCategoryName]);

  const helmetDesc = useMemo(() => {
    if (announcementId) {
      return announcementDetails?.title || "AGCX Announcement Details";
    }
    return `Read updates on ${currentCategoryName || "AGCX announcements"}`;
  }, [announcementId, announcementDetails, currentCategoryName]);

  const stripHtml = (html) => {
    if (!html) return "";
    let tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const getCategoryIconClass = (item) => {
    const key = `${item?._id || ""} ${item?.title || ""}`.toLowerCase();
    if (key.includes("event")) return "ri-calendar-event-line";
    if (key.includes("delist")) return "ri-indeterminate-circle-line";
    if (key.includes("listing") || key.includes("new list")) return "ri-list-check-2";
    if (key.includes("product") || key.includes("update")) return "ri-refresh-line";
    if (key.includes("vip") || key.includes("institution")) return "ri-vip-crown-line";
    if (key.includes("maintenance") || key.includes("system")) return "ri-tools-line";
    if (key.includes("web3")) return "ri-global-line";
    if (key.includes("other")) return "ri-apps-2-line";
    if (key.includes("history")) return "ri-history-line";
    if (key.includes("announce") || key.includes("latest")) return "ri-megaphone-line";
    return "ri-notification-3-line";
  };

  return (
    <>
      <Helmet>
        <title>Announcements | Latest AGCX News & Updates</title>
        <meta name="description" content="Stay up to date with the latest AGCX announcements — new listings, product launches, fee changes, and platform updates. " />
        <meta
          name="keywords"
          content="AGCX announcements, crypto exchange news UAE "
        />
        <link rel="canonical" href="https://arabglobal.ae/announcement" />
      </Helmet>

      {/* RENDER VIEW BASED ON PARAMETERS */}
      {announcementId ? (
        /* 1. ANNOUNCEMENT DETAILS VIEW */
        <section className="announcement_single_layout">
          <div className="container announcement_flex_container">
            {/* Left Main Content */}
            <div className="single_announcement_left">
              <div className="breadcrumb_nav">
                <Link to="/" style={{ color: "inherit", textDecoration: "none" }}>Home</Link> / <Link to="/announcement" style={{ color: "inherit", textDecoration: "none" }}>Announcements</Link> / <span>{announcementDetails?.title || "----"}</span>
              </div>

              <div className="announcement_badge">Announcement</div>

              <h1 className="single_title">{announcementDetails?.title || "----"}</h1>

              <div className="single_meta_row">
                <span className="single_date">
                  {moment(announcementDetails.createdAt).format("MMM DD, YYYY HH:mm:ss")}
                </span>
                <button type="button" className="share_btn" onClick={shareAnnouncement}>Share</button>
              </div>

              {detailCover ? (
                <div className="single_main_img">
                  <ImgWithFallback
                    src={detailCover}
                    fallback={FALLBACK_DETAIL_IMG}
                    alt={announcementDetails?.title || "Announcement"}
                  />
                </div>
              ) : null}

              <div
                className="single_content_block"
                dangerouslySetInnerHTML={{
                  __html: ` ${announcementDetails?.description || ""}`,
                }}
              ></div>

              {/*
              <hr className="single_divider" />

              <div className="follow_us_section">
                <h3>Follow Us</h3>
                <div className="social_grid">
                  <a href="https://x.com/AGCX_official" target="_blank" rel="noopener noreferrer" className="social_box">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
                    </svg>
                    <span>X (Twitter)</span>
                  </a>
                  <a href="https://www.instagram.com/agcx_official/" target="_blank" rel="noopener noreferrer" className="social_box">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#E4405F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                    </svg>
                    <span>Instagram</span>
                  </a>
                  <a href="https://in.linkedin.com/company/arab-global-crypto-exchange-uae" target="_blank" rel="noopener noreferrer" className="social_box">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0A66C2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z"></path>
                      <rect x="2" y="9" width="4" height="12"></rect>
                      <circle cx="4" cy="4" r="2"></circle>
                    </svg>
                    <span>LinkedIn</span>
                  </a>
                  <a href="https://arabglobal.ae/" target="_blank" rel="noopener noreferrer" className="social_box">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FF0000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33 2.78 2.78 0 0 0 1.94 2c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.33 29 29 0 0 0-.46-5.33z"></path>
                      <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon>
                    </svg>
                    <span>YouTube</span>
                  </a>
                  <a href="https://arabglobal.ae/" target="_blank" rel="noopener noreferrer" className="social_box">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="6" cy="12" r="5"></circle>
                      <ellipse cx="16" cy="12" rx="3" ry="5"></ellipse>
                      <ellipse cx="21" cy="12" rx="1" ry="5"></ellipse>
                    </svg>
                    <span>Medium</span>
                  </a>
                  <a href="https://arabglobal.ae/" target="_blank" rel="noopener noreferrer" className="social_box">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#10A37F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="2" y1="12" x2="22" y2="12"></line>
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                    </svg>
                    <span>Website</span>
                  </a>
                </div>
              </div>
              */}
            </div>

            {/* Right Sidebar */}
            <div className="single_announcement_right">
              <div className="sidebar_box top_articles_box">
                <h3>Top Articles</h3>
                <ul>
                  {relatedAnnouncement && relatedAnnouncement.length > 0 ? (
                    relatedAnnouncement.map((item) => (
                      <li key={item?._id}>
                        <h5>
                          <a
                            href={`/announcement/details/${item?._id}`}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/announcement/details/${item?._id}`);
                            }}
                          >
                            {item?.title}
                          </a>
                        </h5>
                        <span className="sidebar_date">
                          {moment(item?.createdAt).format("MMM DD, YYYY HH:mm:ss")}
                        </span>
                      </li>
                    ))
                  ) : (
                    <p style={{ color: "#787878", fontSize: "14px", margin: 0 }}>No related articles.</p>
                  )}
                </ul>
              </div>

              <div className="sidebar_box rss_feed_box">
                <h3>RSS Feed</h3>
                <button className="rss_btn">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="2" y1="12" x2="22" y2="12"></line>
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                  </svg>
                  English RSS
                </button>
                <button className="rss_btn" style={{ marginTop: "16px" }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"></path>
                    <line x1="12" y1="6" x2="12" y2="18"></line>
                    <line x1="8" y1="12" x2="16" y2="12"></line>
                  </svg>
                  中文 RSS
                </button>
              </div>
            </div>
          </div>
        </section>
      ) : (
        /* 2. ANNOUNCEMENT NEW LAYOUT (LIST & CATEGORIES) */
        <section className="announcement_new_layout">
          <div className="container announcement_flex_container">
            {/* Left Sidebar */}
            <div className={`announcement_sidebar_new ${isSidebarOpen ? "open" : ""}`}>
              <ul>
                {categoryList?.map((item) => (
                  <li key={item?._id} className={item?._id === activeCategoryId ? "active" : ""}>
                    <button type="button" onClick={() => { setIsSidebarOpen(false); navigate(`/announcement/${item?._id}`); }}>
                      <i className={getCategoryIconClass(item)} aria-hidden="true" />
                      <span>{item?.title}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Sidebar Overlay for Mobile */}
            {isSidebarOpen && (
              <div className="sidebar_overlay" onClick={() => setIsSidebarOpen(false)}></div>
            )}

            {/* Right Main Content */}
            <div className="announcement_main_area">
              {/* Header */}
              <div className="announcement_main_header">
                <div className="header_text">
                  <button className="mobile_sidebar_toggle" onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="3" y1="12" x2="21" y2="12"></line>
                      <line x1="3" y1="6" x2="21" y2="6"></line>
                      <line x1="3" y1="18" x2="21" y2="18"></line>
                    </svg>
                    Categories
                  </button>
                  <h1>Latest <span>Announcements</span></h1>
                  <p>Stay updated with the latest news from AGCX.</p>
                </div>
                <div className="header_image">
                  <img className="dark_img" src="/images/AnnouncementImg/hand_speaker_img.png" alt="" />
                  <img className="light_img" src="/images/AnnouncementImg/hand_speaker_img_light.png" alt="" />
                </div>
              </div>

              {/* Search */}
              <div className="announcement_search_wrapper">
                <div className="search_input_box">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="#949AA4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M21 21L16.65 16.65" stroke="#949AA4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search announcements..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <button className="search_btn">Search</button>
              </div>

              {/* Announcement List */}
              <div className="announcement_list_container">
                {pagedAnnouncements && pagedAnnouncements.length > 0 ? (
                  pagedAnnouncements.map((ann) => (
                    <div className="announcement_card" key={ann?._id}>
                      <div className="card_left">
                        <h4>
                          {ann?.isNew && <span className="new_badge">NEW</span>}
                          <a
                            href={`/announcement/details/${ann?._id}`}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/announcement/details/${ann?._id}`);
                            }}
                          >
                            {ann?.title}
                          </a>
                        </h4>
                        <p className="card_desc">{stripHtml(ann?.description || "")}</p>
                      </div>
                      <div className="card_right">
                        <div className="card_date_time">
                          <span className="date">{moment(ann.createdAt).format("MMM DD, YYYY")}</span>
                          <span className="time">{moment(ann.createdAt).format("HH:mm:ss")}</span>
                        </div>
                        <button className="card_arrow_btn" onClick={(e) => {
                          e.preventDefault();
                          navigate(`/announcement/details/${ann?._id}`);
                        }}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5 12H19" stroke="#949AA4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M12 5L19 12L12 19" stroke="#949AA4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="announcement-list-empty" role="status">
                    <div className="announcement-empty-inner">
                      <div className="announcement-empty-illustration">
                        <img src={FALLBACK_EMPTY_IMG} className="img-fluid" width={96} height={96} alt="" />
                      </div>
                      <p className="announcement-empty-text">No announcements found for this tab.</p>
                    </div>
                  </div>
                )}
              </div>

              {filteredAnnouncements.length > 0 && (
                <div className="announcement_pagination">
                  <button
                    type="button"
                    className={`page_nav ${page <= 1 ? "disabled" : ""}`}
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15 18L9 12L15 6" stroke="#949AA4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  {pageNumbers.map((n, i) => {
                    const prev = pageNumbers[i - 1];
                    return (
                      <React.Fragment key={n}>
                        {prev && n - prev > 1 ? <span className="page_dots">...</span> : null}
                        <button
                          type="button"
                          className={`page_num ${page === n ? "active" : ""}`}
                          onClick={() => setPage(n)}
                        >
                          {n}
                        </button>
                      </React.Fragment>
                    );
                  })}
                  <button
                    type="button"
                    className={`page_nav ${page >= totalPages ? "disabled" : ""}`}
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 18L15 12L9 6" stroke="#949AA4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              )}

            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default Announcement;